/**
 * Template description split (S24 polish) — the catalog writes the "who may
 * run this" caveat into the SAME `description` string as the differentiating
 * first sentence ("… Requires VIEW SERVER STATE", "… Full detail on other
 * sessions requires pg_monitor / pg_read_all_stats"). Three of four cards in a
 * category then repeat that identical sentence verbatim, drowning the part
 * that actually distinguishes them and making the card grid ragged.
 *
 * This is a DISPLAY-layer split (the catalog stays the single source of
 * truth): the lead sentence(s) stay on the card, the requirement sentence(s)
 * move behind a lock glyph + tooltip, and the detail pane shows both.
 *
 * Guard: the FIRST sentence is never treated as a requirement — some
 * descriptions carry the caveat inside their only sentence ("…full visibility
 * needs the PROCESS privilege"), and an empty lead would be worse than a long
 * one.
 */

/** Sentence boundary: `. ` (or `! `/`? `) followed by a capital / quote / paren. */
const SENTENCE_BOUNDARY = /(?<=[.!?])\s+(?=["'(“A-Z])/;

/** Grant/permission vocabulary — matches the catalog's documented caveats. */
const REQUIREMENT = /\b(requires?|required|privileges?|permissions?|grants?)\b/i;

export type TemplateDescriptionParts = {
  /** Always non-empty — what the template is FOR. */
  lead: string;
  /** Grant/permission caveat, or undefined when the description carries none. */
  requirement?: string;
};

export function splitTemplateDescription(description: string): TemplateDescriptionParts {
  const sentences = description.trim().split(SENTENCE_BOUNDARY).filter(Boolean);
  if (sentences.length < 2) return { lead: description.trim() };

  const lead: string[] = [sentences[0]];
  const requirement: string[] = [];
  for (const sentence of sentences.slice(1)) {
    if (REQUIREMENT.test(sentence)) requirement.push(sentence);
    else lead.push(sentence);
  }
  return {
    lead: lead.join(' '),
    ...(requirement.length > 0 ? { requirement: requirement.join(' ') } : {}),
  };
}
